/**
 * The institute's own name and logo, for the pages that run before sign-in.
 *
 * *** WHY NOT FROM THE SESSION ***
 * `tenant_name` and `tenant_logo_url` ride on a signed-in session, which is
 * how the portal's sidebar shows a client their own mark. Sign-in and
 * sign-up happen before there is one, so those pages showed OURS — a
 * student at their institute's own address saw MentorsConnect branding.
 *
 * Deliberately not React Query: this is one request on an unauthenticated
 * page, outside the provider tree in no configuration we ship but with no
 * caching worth the dependency either.
 */

import { useEffect, useState } from 'react'

export interface InstituteBrand {
  name: string
  slug: string
  logo_url: string | null
}

export interface BrandState {
  brand: InstituteBrand | null
  /** True until the first answer. The page shows no name rather than ours. */
  loading: boolean
}

export function useInstituteBrand(slug: string | undefined): BrandState {
  const [brand, setBrand] = useState<InstituteBrand | null>(null)
  const [loading, setLoading] = useState<boolean>(Boolean(slug))

  useEffect(() => {
    if (!slug) {
      setBrand(null)
      setLoading(false)
      return
    }

    /*
      *** THE ANSWER MUST BE DISCARDED IF THE SLUG CHANGED ***
      Two institutes open in quick succession would otherwise race, and the
      slower reply would paint the wrong name over the right one.
    */
    let current = true
    setLoading(true)

    /*
      *** NO /api/v1 HERE, DELIBERATELY ***
      `merchant_page` is mounted at the application root, not under the API
      prefix, because the merchant page beside it is a URL a bank reviewer
      opens in a browser. `/api/v1/institute/...` returns 401 — the generic
      deny-by-default answer for a path no public rule matches, which reads
      exactly like a missing auth-policy entry and is not one.
    */
    fetch(`/institute/${encodeURIComponent(slug)}/brand`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data: InstituteBrand | null) => {
        if (current) setBrand(data)
      })
      .catch(() => {
        // A failed lookup is not a failed page. The form still works; it
        // just carries no institute name.
        if (current) setBrand(null)
      })
      .finally(() => {
        if (current) setLoading(false)
      })

    return () => {
      current = false
    }
  }, [slug])

  return { brand, loading }
}
